/**
 * formSubmit — the Effect core for visitor form submission, shared so web + RN
 * use one validated, tagged-error submit path (Effect everywhere; no ad-hoc
 * fetch). Submissions are a visitor-time POST straight to builder-api — they
 * never touch the editor-sync or publish path, so the local-first invariant
 * (form CONFIG lives in the Yjs doc) is untouched.
 *
 * Errors are Data.TaggedError, never Error subclasses: FormValidationError
 * carries the offending field so native can highlight that input (RN has no
 * browser "required" bubble); FormHttpError carries the status.
 */
import { Effect, Data } from "effect";
import { HttpClient, HttpClientRequest } from "@effect/platform";
import type { FormWidgetContent } from "@website-builder/schemas";

export type FormField = FormWidgetContent["fields"][number];

/** Canonical checkbox wire value — matches the web published-HTML default
 *  (`<input type=checkbox>` submits "on"), so the backend jsonb + email payload
 *  stay uniform across web and native. */
export const CHECKBOX_ON = "on";

export class FormValidationError extends Data.TaggedError(
  "FormValidationError",
)<{
  readonly field: string;
  readonly message: string;
}> {}

export class FormHttpError extends Data.TaggedError("FormHttpError")<{
  readonly status: number;
  readonly message: string;
}> {}

function isFilled(field: FormField, value: string): boolean {
  return field.type === "checkbox"
    ? value === CHECKBOX_ON
    : value.trim().length > 0;
}

/** Fail with the FIRST missing required field, else succeed. */
export const validateRequired = (
  fields: readonly FormField[],
  values: Record<string, string>,
): Effect.Effect<void, FormValidationError> =>
  Effect.gen(function* () {
    for (const f of fields) {
      if (f.required && !isFilled(f, values[f.name] ?? "")) {
        return yield* new FormValidationError({
          field: f.name,
          message: `${f.label} is required`,
        });
      }
    }
  });

export function buildPayload(
  fields: readonly FormField[],
  values: Record<string, string>,
): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const f of fields) payload[f.name] = values[f.name] ?? "";
  return payload;
}

export interface SubmitFormInput {
  readonly baseUrl: string;
  readonly siteId: string;
  readonly itemId: string;
  readonly fields: readonly FormField[];
  readonly values: Record<string, string>;
  /** Honeypot value; non-empty ⇒ a bot, silently "succeed". */
  readonly honeypot: string;
}

/** Validate → POST. Requires an HttpClient in context (FetchHttpClient on RN). */
export const submitForm = (
  input: SubmitFormInput,
): Effect.Effect<
  void,
  FormValidationError | FormHttpError,
  HttpClient.HttpClient
> =>
  Effect.gen(function* () {
    if (input.honeypot.length > 0) return;
    yield* validateRequired(input.fields, input.values);

    const client = yield* HttpClient.HttpClient;
    const url = `${input.baseUrl}/api/v1/sites/${input.siteId}/forms/${input.itemId}`;
    const req = HttpClientRequest.post(url).pipe(
      HttpClientRequest.bodyUnsafeJson(
        buildPayload(input.fields, input.values),
      ),
    );

    const res = yield* client
      .execute(req)
      .pipe(
        Effect.mapError(
          (e) => new FormHttpError({ status: 0, message: e.message }),
        ),
      );
    if (res.status >= 400) {
      return yield* new FormHttpError({
        status: res.status,
        message: `Submission failed (HTTP ${res.status})`,
      });
    }
  });
